import { useCallback, useRef, useState } from "react";
import { api } from "./api";
import {
  applyNewestHistoryPage,
  mergeOlderHistoryPage,
  shouldAcceptHistoryResponse,
  shouldClearStoredConversation,
} from "./conversationHistoryUi";

/** Newest-page history load + explicit older pages; ignores stale responses. */
export function useConversationHistory({ tenantId, activeConvKey, abortRef, runIdRef }) {
  const [messages, setMessages] = useState([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const historyReqRef = useRef(0);

  const resetHistory = useCallback(() => {
    historyReqRef.current++;
    setMessages([]);
    setHistoryHasMore(false);
    setHistoryCursor(null);
    setHistoryLoading(false);
  }, []);

  const loadConversation = useCallback(async (cid, { onLiveReset, onOpened, onError } = {}) => {
    abortRef.current?.abort();
    runIdRef.current++;
    const reqId = ++historyReqRef.current;
    onLiveReset?.();
    setHistoryLoading(true);
    try {
      const page = await api.conversationTurns(tenantId, cid, { limit: 50 });
      if (!shouldAcceptHistoryResponse(reqId, historyReqRef.current)) return;
      const applied = applyNewestHistoryPage(page);
      setMessages(applied.messages);
      setHistoryHasMore(applied.hasMore);
      setHistoryCursor(applied.cursor);
      // Durable restore key only after owner-load succeeds.
      localStorage.setItem(activeConvKey, cid);
      onOpened?.(cid, applied.messages);
    } catch (e) {
      // Stale A must not undo successful B.
      if (!shouldAcceptHistoryResponse(reqId, historyReqRef.current)) return;
      const status = e?.response?.status;
      let clearedActive = false;
      if (status === 404 && shouldClearStoredConversation(localStorage.getItem(activeConvKey), cid)) {
        localStorage.removeItem(activeConvKey);
        clearedActive = true;
      }
      onError?.("Could not load conversation", { status, cid, clearedActive });
    } finally {
      if (shouldAcceptHistoryResponse(reqId, historyReqRef.current)) setHistoryLoading(false);
    }
  }, [tenantId, activeConvKey, abortRef, runIdRef]);

  const loadOlderMessages = useCallback(async (conversationId, { onError } = {}) => {
    if (!historyHasMore || !historyCursor || !conversationId || historyLoading) return;
    const reqId = historyReqRef.current;
    setHistoryLoading(true);
    try {
      const page = await api.conversationTurns(tenantId, conversationId, {
        limit: 50,
        before_ts: historyCursor.before_ts,
        before_id: historyCursor.before_id,
      });
      if (!shouldAcceptHistoryResponse(reqId, historyReqRef.current)) return;
      setMessages((prev) => {
        const merged = mergeOlderHistoryPage(prev, page);
        setHistoryHasMore(merged.hasMore);
        setHistoryCursor(merged.cursor);
        return merged.messages;
      });
    } catch (e) {
      if (!shouldAcceptHistoryResponse(reqId, historyReqRef.current)) return;
      onError?.("Could not load older messages");
    } finally {
      if (shouldAcceptHistoryResponse(reqId, historyReqRef.current)) setHistoryLoading(false);
    }
  }, [tenantId, historyHasMore, historyCursor, historyLoading]);

  return {
    messages, setMessages, historyHasMore, historyLoading,
    loadConversation, loadOlderMessages, resetHistory,
  };
}
